import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, User, Mail, Phone, Building2, MapPin, Globe, FileText,
  TrendingUp, DollarSign, Shield, Save, X, Briefcase, Award, Edit3,
  CheckCircle, Clock, Tag, Bell
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LuxuryBackground, Card, GoldButton, PageHero } from "../components/shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanTypeLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
import { stageLabel, stageBadgeColor } from "../lib/stageConstants";

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "overview";
  });
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile`)
      .then(r => { setProfile(r.data); setForm(r.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
    axios.get(`${API}/notifications/preferences`)
      .then(r => setNotifPrefs(r.data))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const key of ["full_name", "phone", "company", "bio", "location", "specialties", "website", "lending_criteria"]) {
        if (form[key] !== undefined && form[key] !== profile[key]) payload[key] = form[key];
      }
      if (Object.keys(payload).length > 0) {
        const token = localStorage.getItem("ic_token");
        const res = await axios.put(`${API}/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(prev => ({ ...prev, ...res.data.user }));
        setForm(prev => ({ ...prev, ...res.data.user }));
        toast.success("Profile updated successfully");
      } else {
        toast.info("No changes to save");
      }
      setEditing(false);
      setActiveTab("overview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    }
    finally { setSaving(false); }
  };

  const togglePref = async (key) => {
    const newVal = !notifPrefs[key];
    setNotifPrefs(prev => ({ ...prev, [key]: newVal }));
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/notifications/preferences`, { [key]: newVal });
    } catch (err) {
      setNotifPrefs(prev => ({ ...prev, [key]: !newVal }));
    } finally { setSavingPrefs(false); }
  };

  if (loading) return (
    <div className="relative min-h-[60vh] flex items-center justify-center"><LuxuryBackground />
      <Loader2 className="relative z-10 h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  const role = profile?.role || "borrower";
  const stats = profile?.stats || {};
  const initials = (profile?.full_name || "U").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="relative" data-testid="profile-page">
      <PageHero eyebrow="Profile" title={profile?.full_name || "My Profile"}
        description={role === "borrower" ? "Manage your profile and track your lending journey" : role === "admin" ? "Administrator profile and platform oversight" : "Manage your lending profile and deal activity"} />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          {/* Profile Header Card */}
          <Card hover={false} className="mb-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gold/30 via-emerald-500/20 to-gold/10 border-2 border-cream-warm flex items-center justify-center shrink-0">
                <span className="text-2xl font-bold text-gold-dark">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0A0A0A]" data-testid="profile-name">{profile?.full_name}</h2>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        role === "admin" ? "bg-red-100 text-red-600 border border-red-400/30" :
                        role === "lender" || role === "investor" ? "bg-blue-100 text-blue-600 border border-blue-400/30" :
                        "bg-emerald-100 text-emerald-600 border border-emerald-200"
                      }`} data-testid="profile-role">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                      {profile?.company && <span className="text-sm text-[#555] flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{profile.company}</span>}
                      {profile?.location && <span className="text-sm text-[#555] flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile.location}</span>}
                    </div>
                    {profile?.bio && <p className="mt-3 text-sm text-[#444] max-w-xl">{profile.bio}</p>}
                  </div>
                </div>

                {/* Quick Info Row */}
                <div className="flex gap-4 mt-4 flex-wrap text-sm text-[#555]">
                  <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-emerald-700/70" />{profile?.email}</span>
                  {profile?.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-emerald-700/70" />{profile.phone}</span>}
                  {profile?.website && <span className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-emerald-700/70" />{profile.website}</span>}
                  {profile?.created_at && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-emerald-700/70" />Joined {new Date(profile.created_at).toLocaleDateString()}</span>}
                </div>
              </div>
            </div>
          </Card>

          {/* Stats Row */}
          <StatsRow role={role} stats={stats} />

          {/* Tabs */}
          <div className="flex gap-2 mb-6 mt-8 flex-wrap">
            {[
              { key: "overview", label: "Overview" },
              { key: "edit", label: "Edit Profile" },
              ...(role !== "admin" ? [{ key: "documents", label: "Documents" }] : []),
              { key: "notifications", label: "Notifications" },
            ].map(t => (
              <button key={t.key} onClick={() => { setActiveTab(t.key); if (t.key === "edit") setEditing(true); }}
                data-testid={`profile-tab-${t.key}`}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === t.key ? "bg-gold text-[#022c22]" : "bg-cream text-[#444] hover:text-[#0A0A0A] border border-cream-warm"
                }`}>{t.label}</button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === "overview" && (
              <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <OverviewTab role={role} profile={profile} />
              </motion.div>
            )}
            {(activeTab === "edit" || editing) && (
              <motion.div key="edit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <EditForm form={form} setForm={setForm} role={role} onSave={handleSave} saving={saving} />
              </motion.div>
            )}
            {activeTab === "notifications" && notifPrefs && (
              <motion.div key="notifs" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <NotificationPrefs prefs={notifPrefs} onToggle={togglePref} saving={savingPrefs} />
              </motion.div>
            )}
            {activeTab === "documents" && role !== "admin" && (
              <motion.div key="documents" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <DocumentChecklist checklist={profile?.doc_checklist} isVerified={profile?.is_verified} role={role}
                  onRefresh={() => axios.get(`${API}/profile`).then(r => { setProfile(r.data); setForm(r.data); }).catch(console.error)} />
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </div>
    </div>
  );
}

function StatsRow({ role, stats }) {
  const items = role === "borrower" ? [
    { label: "Applications", value: stats.total_applications || 0, icon: FileText, color: "text-blue-600" },
    { label: "Funded Deals", value: stats.funded_deals || 0, icon: CheckCircle, color: "text-emerald-600" },
    { label: "Total Volume", value: `$${((stats.total_volume || 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
    { label: "Active Deals", value: stats.active_deals || 0, icon: TrendingUp, color: "text-amber-600" },
  ] : role === "admin" ? [
    { label: "Total Users", value: stats.total_users || 0, icon: User, color: "text-blue-600" },
    { label: "Total Deals", value: stats.total_deals || 0, icon: FileText, color: "text-emerald-600" },
    { label: "Term Sheets", value: stats.total_term_sheets || 0, icon: Briefcase, color: "text-gold-dark" },
    { label: "Funded Deals", value: stats.funded_deals || 0, icon: Award, color: "text-amber-600" },
  ] : [
    { label: "Deals Reviewed", value: stats.deals_reviewed || 0, icon: FileText, color: "text-blue-600" },
    { label: "Term Sheets", value: stats.term_sheets_submitted || 0, icon: Briefcase, color: "text-emerald-600" },
    { label: "Total Offered", value: `$${((stats.total_offered || 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
    { label: "Active Offers", value: stats.active_offers || 0, icon: TrendingUp, color: "text-amber-600" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item, i) => (
        <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
          <Card hover={false}>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm">
                <item.icon className={`h-5 w-5 ${item.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A0A0A]">{item.value}</div>
                <div className="text-xs text-[#888]">{item.label}</div>
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

function OverviewTab({ role, profile }) {
  if (role === "borrower" && profile?.recent_deals?.length > 0) {
    return (
      <Card hover={false}>
        <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-gold-dark" /> Recent Applications</h3>
        <div className="space-y-3">
          {profile.recent_deals.map((deal, i) => (
            <div key={deal.lead_id || i} className="flex items-center justify-between p-4 rounded-xl border border-cream-warm bg-cream">
              <div>
                <div className="text-sm font-medium text-[#0A0A0A]">{deal.property_address || "Untitled Deal"}</div>
                <div className="text-xs text-[#888] mt-0.5">{loanTypeLabels[deal.loan_type] || deal.loan_type} &bull; ${(deal.purchase_price || 0).toLocaleString()}</div>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${stageBadgeColor(deal.stage)}`}>{stageLabel(deal.stage)}</span>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if ((role === "lender" || role === "investor") && profile?.recent_term_sheets?.length > 0) {
    return (
      <Card hover={false}>
        <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-gold-dark" /> Recent Term Sheets</h3>
        <div className="space-y-3">
          {profile.recent_term_sheets.map((ts, i) => (
            <div key={ts.ts_id || i} className="flex items-center justify-between p-4 rounded-xl border border-cream-warm bg-cream">
              <div>
                <div className="text-sm font-medium text-[#0A0A0A]">${(ts.loan_amount || 0).toLocaleString()} at {ts.interest_rate}%</div>
                <div className="text-xs text-[#888] mt-0.5">{ts.created_at ? new Date(ts.created_at).toLocaleDateString() : ""}</div>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                ts.status === "accepted" ? "bg-emerald-100 text-emerald-600" : ts.status === "withdrawn" ? "bg-red-100 text-red-600" : "bg-gold/15 text-gold-dark"
              }`}>{ts.status}</span>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (role === "admin") {
    return (
      <Card hover={false}>
        <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Shield className="h-5 w-5 text-gold-dark" /> Admin Overview</h3>
        <p className="text-[#555] text-sm">As an administrator, you have full oversight of the platform. Use the Admin Dashboard for detailed management of users, deals, and platform analytics.</p>
      </Card>
    );
  }

  return (
    <Card hover={false}>
      <div className="text-center py-8 text-[#999]">
        <User className="h-12 w-12 mx-auto mb-3 text-cream/20" />
        <p>No activity yet. Start by exploring the platform!</p>
      </div>
    </Card>
  );
}

const inputClass = "w-full rounded-xl border border-cream-warm bg-cream px-4 py-3 text-[#0A0A0A] outline-none transition placeholder:text-[#aaa] focus:border-gold/50 focus:ring-2 focus:ring-gold/20 text-sm";
const labelClass = "mb-1.5 block text-xs text-emerald-700 font-medium";

function EditForm({ form, setForm, role, onSave, saving }) {
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Card hover={false} data-testid="profile-edit-form">
      <h3 className="text-lg font-bold text-[#0A0A0A] mb-6 flex items-center gap-2"><Edit3 className="h-5 w-5 text-gold-dark" /> Edit Profile</h3>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Full Name</label>
          <input className={inputClass} value={form.full_name || ""} onChange={e => set("full_name", e.target.value)} data-testid="profile-input-name" />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input className={inputClass} value={form.phone || ""} onChange={e => set("phone", e.target.value)} placeholder="+1 (555) 000-0000" data-testid="profile-input-phone" />
        </div>
        <div>
          <label className={labelClass}>Company</label>
          <input className={inputClass} value={form.company || ""} onChange={e => set("company", e.target.value)} placeholder="Your company name" data-testid="profile-input-company" />
        </div>
        <div>
          <label className={labelClass}>Location</label>
          <input className={inputClass} value={form.location || ""} onChange={e => set("location", e.target.value)} placeholder="City, State" data-testid="profile-input-location" />
        </div>
        <div>
          <label className={labelClass}>Website</label>
          <input className={inputClass} value={form.website || ""} onChange={e => set("website", e.target.value)} placeholder="https://..." data-testid="profile-input-website" />
        </div>
        <div>
          <label className={labelClass}>Specialties</label>
          <input className={inputClass} value={(form.specialties || []).join(", ")} onChange={e => set("specialties", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Fix & Flip, Bridge, DSCR" data-testid="profile-input-specialties" />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Bio</label>
          <textarea className={`${inputClass} min-h-[100px] resize-none`} value={form.bio || ""} onChange={e => set("bio", e.target.value)} placeholder="Tell others about yourself..." data-testid="profile-input-bio" />
        </div>

        {(role === "lender" || role === "investor") && (
          <>
            <div className="md:col-span-2">
              <h4 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2"><Tag className="h-4 w-4 text-gold-dark" /> Lending Criteria</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Min Loan Amount</label>
                  <input type="number" className={inputClass} value={form.lending_criteria?.min_loan || ""} onChange={e => set("lending_criteria", { ...(form.lending_criteria || {}), min_loan: Number(e.target.value) })} placeholder="50000" data-testid="profile-input-min-loan" />
                </div>
                <div>
                  <label className={labelClass}>Max Loan Amount</label>
                  <input type="number" className={inputClass} value={form.lending_criteria?.max_loan || ""} onChange={e => set("lending_criteria", { ...(form.lending_criteria || {}), max_loan: Number(e.target.value) })} placeholder="5000000" data-testid="profile-input-max-loan" />
                </div>
                <div>
                  <label className={labelClass}>Preferred States</label>
                  <input className={inputClass} value={form.lending_criteria?.states || ""} onChange={e => set("lending_criteria", { ...(form.lending_criteria || {}), states: e.target.value })} placeholder="TX, FL, CA" data-testid="profile-input-states" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <GoldButton onClick={onSave} className="!px-8 !py-3 !text-sm" data-testid="profile-save-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2 inline" />Save Changes</>}
        </GoldButton>
      </div>
    </Card>
  );
}


const PREF_SECTIONS = [
  {
    title: "Email Notifications",
    icon: Mail,
    items: [
      { key: "email_deal_updates", label: "Deal status changes", desc: "Get emailed when your deal progresses to a new stage" },
      { key: "email_deal_room", label: "Deal Room messages", desc: "Get emailed when someone sends a message in your Deal Room" },
      { key: "email_term_sheets", label: "New term sheets", desc: "Get emailed when a lender submits an offer on your deal" },
    ],
  },
  {
    title: "In-App Notifications",
    icon: Bell,
    items: [
      { key: "in_app_deal_updates", label: "Deal status changes", desc: "Show notification badge for deal progress updates" },
      { key: "in_app_deal_room", label: "Deal Room messages", desc: "Show notification badge for new messages" },
      { key: "in_app_term_sheets", label: "New term sheets", desc: "Show notification badge for new offers" },
    ],
  },
];

function NotificationPrefs({ prefs, onToggle, saving }) {
  return (
    <div className="space-y-6" data-testid="notification-prefs">
      {PREF_SECTIONS.map((section) => (
        <Card key={section.title} hover={false}>
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
            <section.icon className="h-4.5 w-4.5 text-gold-dark" /> {section.title}
          </h3>
          <div className="space-y-4">
            {section.items.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-[#0A0A0A] font-medium">{item.label}</div>
                  <div className="text-xs text-[#999] mt-0.5">{item.desc}</div>
                </div>
                <button onClick={() => onToggle(item.key)} disabled={saving}
                  data-testid={`pref-toggle-${item.key}`}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${prefs[item.key] ? "bg-gold" : "bg-cream"}`}>
                  <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${prefs[item.key] ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function DocumentChecklist({ checklist, isVerified, role, onRefresh }) {
  const [uploading, setUploading] = useState(null); // doc_type being uploaded
  const fileInputRef = React.useRef(null);
  const [activeUploadType, setActiveUploadType] = useState(null);

  // Polling: refresh doc checklist every 30s when any document is pending_review
  React.useEffect(() => {
    const hasPending = checklist?.some(d => d.uploaded && d.review_status !== "approved" && d.review_status !== "rejected");
    if (!hasPending || !onRefresh) return;
    const interval = setInterval(() => { onRefresh(); }, 30000);
    return () => clearInterval(interval);
  }, [checklist, onRefresh]);

  if (!checklist || checklist.length === 0) {
    return (
      <Card hover={false}>
        <div className="text-center py-8 text-sm text-[#888]" data-testid="no-doc-checklist">
          No document requirements for your account type.
        </div>
      </Card>
    );
  }

  const approvedCount = checklist.filter(d => d.review_status === "approved").length;
  const uploadedCount = checklist.filter(d => d.uploaded).length;
  const roleLabel = role === "investor" ? "Accredited" : "Verified";

  const getStatusConfig = (doc) => {
    if (!doc.uploaded) return { label: "Upload Required", color: "bg-amber-100 text-amber-700 border-amber-200", borderColor: "border-amber-200 bg-amber-50/30", icon: <Clock className="h-5 w-5 text-amber-500 shrink-0" /> };
    if (doc.review_status === "approved") return { label: "Approved", color: "bg-emerald-100 text-emerald-700 border-emerald-200", borderColor: "border-emerald-200 bg-emerald-50/30", icon: <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" /> };
    if (doc.review_status === "rejected") return { label: "Rejected", color: "bg-red-100 text-red-700 border-red-200", borderColor: "border-red-300 bg-red-50/30", icon: <X className="h-5 w-5 text-red-500 shrink-0" /> };
    return { label: "Under Review", color: "bg-blue-100 text-blue-700 border-blue-200", borderColor: "border-blue-200 bg-blue-50/30", icon: <Clock className="h-5 w-5 text-blue-500 shrink-0" /> };
  };

  const handleUpload = async (file, docType) => {
    if (!file) return;
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/documents/profile/upload?doc_type=${docType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Upload failed:", err);
      alert(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(null);
      setActiveUploadType(null);
    }
  };

  const triggerUpload = (docType) => {
    setActiveUploadType(docType);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  return (
    <div className="space-y-4" data-testid="document-checklist">
      {/* Hidden file input */}
      <input type="file" ref={fileInputRef} className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={(e) => { if (e.target.files[0] && activeUploadType) handleUpload(e.target.files[0], activeUploadType); e.target.value = ""; }}
      />

      {/* Verification Status Banner */}
      <Card hover={false}>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${isVerified ? "bg-emerald-100 border border-emerald-200" : approvedCount === checklist.length ? "bg-emerald-50 border border-emerald-200" : uploadedCount === checklist.length ? "bg-blue-50 border border-blue-200" : "bg-[#faf7f2] border border-[#e8e0d0]"}`}>
            {isVerified ? (
              <Shield className="h-6 w-6 text-emerald-600" />
            ) : (
              <FileText className="h-6 w-6 text-[#b89d5a]" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#0A0A0A]" data-testid="verification-status-title">
              {isVerified ? `${roleLabel} — Complete`
                : approvedCount === checklist.length ? "All Documents Approved"
                : uploadedCount === checklist.length ? "Under Review"
                : "Verification Required"}
            </h3>
            <p className="text-xs text-[#666] mt-0.5">
              {isVerified
                ? `Your account is ${roleLabel.toLowerCase()}. All required documents have been accepted.`
                : approvedCount === checklist.length
                  ? "All documents approved. Your account will be verified shortly."
                  : uploadedCount === checklist.length
                    ? "All documents submitted. Our team is reviewing your submission."
                    : `${uploadedCount} of ${checklist.length} documents submitted. Upload your remaining documents to complete verification.`
              }
            </p>
          </div>
          {isVerified && (
            <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200" data-testid="verified-badge">
              {roleLabel}
            </div>
          )}
        </div>
        {/* Progress bar */}
        {!isVerified && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider text-[#999] font-semibold">Progress</span>
              <span className="text-[10px] text-[#666] font-semibold">{approvedCount} approved / {checklist.length} required</span>
            </div>
            <div className="h-2 w-full rounded-full bg-[#e8e0d0] overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${(approvedCount / checklist.length) * 100}%` }} />
            </div>
          </div>
        )}
      </Card>

      {/* Document List */}
      <Card hover={false}>
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Required Documents</h4>
        <div className="space-y-2">
          {checklist.map((doc) => {
            const st = getStatusConfig(doc);
            return (
              <div key={doc.type}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${st.borderColor}`}
                data-testid={`doc-status-${doc.type}`}>
                {st.icon}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#333]">{doc.label}</p>
                  <p className="text-[10px] text-[#999] mt-0.5">
                    {!doc.uploaded ? "Upload your document to begin verification"
                      : doc.review_status === "approved" ? "Document has been reviewed and approved"
                      : doc.review_status === "rejected" ? (doc.review_notes ? `Rejected: ${doc.review_notes}` : "Document was rejected — please re-upload")
                      : "Document submitted — awaiting review"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${st.color}`}>
                    {st.label}
                  </span>
                  {(!doc.uploaded || doc.review_status === "rejected") && (
                    <button
                      onClick={() => triggerUpload(doc.type)}
                      disabled={uploading === doc.type}
                      data-testid={`upload-btn-${doc.type}`}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-[#022c22] text-white hover:bg-[#034d3a] transition-colors disabled:opacity-50"
                    >
                      {uploading === doc.type ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        doc.review_status === "rejected" ? "Re-upload" : "Upload"
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Info note */}
      {!isVerified && (
        <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] px-4 py-3 text-xs text-[#666]" data-testid="doc-info-note">
          <strong>Note:</strong> Verification documents are securely stored and only accessible by our internal compliance team. You will be notified when documents are reviewed. If a document is rejected, you can re-upload directly from here.
        </div>
      )}
    </div>
  );
}
